import React, { useState, useEffect, useCallback, useRef } from "react";
import { ClipLoader } from "react-spinners";
import { useHistory } from "react-router-dom";
import moment from "moment";
import fileDownload from "js-file-download";
import { brMill } from "@react-jvectormap/brazil";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
} from "reactstrap";

import api from "../../../../../services/api";
import { formatCurrency } from "../../../../../utils/formatCurrency";
import { weightFormat } from "../../../../../utils/weightFormat";
import { useTheme } from "../../../../../hooks/theme";
import { useCompanies } from "../../../../../hooks/companies";
import { useToast } from "../../../../../hooks/toast";
import ModalFilters from "../../../../../components/CustomModalFilters";
import SortHeader from "../../../../../components/SortHeader";
import { toggleSort, sortRows } from "../../../../../utils/tableHelpers";
import { AUTOPILOT_COLORS } from "../../../../../components/autopilot/styles";

const { width: MAP_W, height: MAP_H, paths } = brMill.content;

const COLOR_START = { r: 179, g: 229, b: 209 };
const COLOR_END   = { r:  29, g: 140, b: 248 };
const COLOR_SEL   = "#f5365c";

function interpolateColor(t) {
  const r = Math.round(COLOR_START.r + (COLOR_END.r - COLOR_START.r) * t);
  const g = Math.round(COLOR_START.g + (COLOR_END.g - COLOR_START.g) * t);
  const b = Math.round(COLOR_START.b + (COLOR_END.b - COLOR_START.b) * t);
  return `rgb(${r},${g},${b})`;
}

const fmtInt = (v) => Number(v).toLocaleString("pt-BR");
const fmtPercent2 = (v) => `${Number(v).toFixed(2)}%`;
const fmtFreteKg = (v) => `R$ ${Number(v).toFixed(2)}/kg`;

// PO1-2635 (revisão pós-teste) — cada aba recolore o mapa por um campo E
// mostra só as colunas necessárias pra entender aquela métrica (não fica
// exibindo Peso/Orçado numa aba que não tem nada a ver com eles). O
// "todas as métricas juntas" do pedido original do cliente vira o botão
// Exportar (XLSX com tudo), não a tabela na tela.
const COLUMN_DEFS = {
  qtdCte: { label: "Qtd. CTe", format: fmtInt },
  valorFrete: { label: "Valor do Frete", format: formatCurrency },
  valorMercadoria: { label: "Valor Mercadoria", format: formatCurrency },
  peso: { label: "Peso", format: weightFormat },
  freteNf: { label: "Frete/NF", format: fmtPercent2 },
  freteKg: { label: "Frete/Kg", format: fmtFreteKg },
  orcado: { label: "Orçado", format: formatCurrency },
  percentualExecutado: { label: "% Executado", format: fmtPercent2 },
};

const METRICS = [
  {
    key: "valorFrete",
    label: "Valor do Frete",
    getValue: (r) => r.valorFrete,
    format: (v) => formatCurrency(v),
    emptyLabel: "R$ 0,00",
    // quantidade dá o contexto de volume por trás do valor total
    columns: ["qtdCte", "valorFrete"],
  },
  {
    key: "freteNf",
    label: "Frete / NF",
    getValue: (r) => r.freteNf,
    format: (v) => fmtPercent2(v),
    emptyLabel: "0,00%",
    // razão precisa do numerador, denominador e volume pra não enganar
    // (motivo original do épico: % alto do PA eram só 6 CT-es)
    columns: ["qtdCte", "valorFrete", "valorMercadoria", "freteNf"],
  },
  {
    key: "qtdCte",
    label: "Qtd. CTe",
    getValue: (r) => r.qtdCte,
    format: (v) => fmtInt(v),
    emptyLabel: "0",
    columns: ["qtdCte", "valorFrete"],
  },
  {
    key: "freteKg",
    label: "Frete / Kg",
    getValue: (r) => r.freteKg,
    format: (v) => fmtFreteKg(v),
    emptyLabel: "R$ 0,00/kg",
    // razão precisa do numerador (frete), denominador (peso) e volume
    columns: ["qtdCte", "valorFrete", "peso", "freteKg"],
  },
  {
    key: "orcado",
    label: "Orçado",
    getValue: (r) => r.orcado,
    format: (v) => formatCurrency(v),
    emptyLabel: "R$ 0,00",
    // comparação orçado x realizado só faz sentido com os dois + o %
    columns: ["qtdCte", "valorFrete", "orcado", "percentualExecutado"],
  },
];

export default function MapFretePorUF({ deferLoad = false, onRegisterFetch }) {
  const { darkMode } = useTheme();
  const { empresa, transportadora, arrayParams } = useCompanies();
  const history = useHistory();
  const { addToast } = useToast();

  const [activeMetric, setActiveMetric] = useState(0);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [tooltip, setTooltip] = useState(null);
  const [selected, setSelected] = useState(null);
  // PO1-3748: o clique na linha abre o detalhe da UF num modal (antes navegava
  // para a listagem de cotações e o usuário perdia o mapa e o filtro).
  const [detalheUf, setDetalheUf] = useState(null);
  const [modalFilters, setModalFilters] = useState(false);
  const [dataInicial, setDataInicial] = useState(
    moment().subtract(90, "days").startOf("month")
  );
  const [dataFinal, setDataFinal] = useState(moment().endOf("month"));
  const [auxDtInicial, setAuxDtInicial] = useState(
    moment().subtract(90, "days").startOf("month")
  );
  const [auxDtFinal, setAuxDtFinal] = useState(moment().endOf("month"));
  // PO1-2635 — ordenação por coluna, default "Valor do Frete desc"
  // (critério de aceite); trocar de aba reordena pela métrica ativa.
  const [sort, setSort] = useState({ field: "valorFrete", dir: "desc" });
  const svgRef = useRef(null);

  function buildParams() {
    const params = {
      dataInicial: auxDtInicial.format("YYYY-MM-DD 00:00:00"),
      dataFinal: auxDtFinal.format("YYYY-MM-DD 23:59:59"),
    };
    if (arrayParams.embarcadores) params.embarcadores = arrayParams.embarcadores;
    if (arrayParams.transportadoras) params.transportadoras = arrayParams.transportadoras;
    return params;
  }

  // PO1-2635 — 1 fetch só por período/filtro (endpoint consolidado
  // PO1-2634 + Orçado, que é de outra fonte/módulo — GoBID Savings/Mongo,
  // por isso continua sendo uma chamada separada). Trocar de aba não
  // busca nada de novo (critério de aceite do PO1-2635).
  async function loadCharts() {
    try {
      setLoading(true);
      const params = buildParams();

      const [metricasRes, orcadoRes] = await Promise.all([
        api.get("/dashboard/custo/frete-por-uf-metricas", { params }),
        api.get("/dashboard/custo/orcado-por-uf", { params }).catch(() => ({ data: [] })),
      ]);

      if (metricasRes.status === 200) {
        const orcadoBySigla = {};
        (Array.isArray(orcadoRes.data) ? orcadoRes.data : []).forEach((item) => {
          if (item && item.sigla) {
            orcadoBySigla[item.sigla.toUpperCase()] = item.valor != null ? Number(item.valor) : null;
          }
        });

        const merged = metricasRes.data.map((row) => {
          const orcado = orcadoBySigla[row.sigla] != null ? orcadoBySigla[row.sigla] : null;
          const percentualExecutado =
            orcado != null && orcado !== 0 && row.valorFrete != null
              ? (row.valorFrete / orcado) * 100
              : null;
          return { ...row, orcado, percentualExecutado };
        });

        setData(merged);
        setSelected(null);
      }
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (onRegisterFetch) onRegisterFetch(loadCharts);
    if (!deferLoad) {
      if (empresa) loadCharts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auxDtFinal, auxDtInicial, empresa, transportadora]);

  // PO1-2635 — só troca a métrica ativa (recolore mapa + destaca coluna);
  // não refaz fetch nem altera as linhas da tabela.
  const handleMetricChange = (idx) => {
    setActiveMetric(idx);
    setSort({ field: METRICS[idx].key, dir: "desc" });
  };

  const handleSort = (field) => setSort((current) => toggleSort(current, field));

  // Exporta o recorte atual (período + filtros do header) em XLSX, com o
  // detalhe CT-e a CT-e (PO1-2636). Com um estado destacado, exporta só ele.
  const handleExport = async () => {
    try {
      setExporting(true);
      const dataInicialSlug = auxDtInicial.format("YYYY-MM-DD");
      const dataFinalSlug = auxDtFinal.format("YYYY-MM-DD");
      const params = buildParams();
      if (selected) params.uf = selected.toUpperCase();

      const response = await api.get("/dashboard/custo/frete-por-uf-export", {
        params,
        responseType: "blob",
      });
      fileDownload(
        response.data,
        `frete-por-uf-${dataInicialSlug}-a-${dataFinalSlug}${
          selected ? `-${selected.toUpperCase()}` : ""
        }.xlsx`
      );
    } catch (error) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Exportar XLSX",
        description: "Falha ao gerar a planilha.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setExporting(false);
    }
  };

  const metric = METRICS[activeMetric];
  const activeColumns = metric.columns.map((field) => ({ field, ...COLUMN_DEFS[field] }));

  // UF sem operação nenhuma (Qtd. CTe = 0) não deve colorir o mapa como se
  // tivesse o menor valor da escala — fica igual às métricas que já vêm
  // null (Frete/NF, Frete/Kg): cinza neutro (sem dado), não ponta da
  // escala de cor. Sem isso, "Valor do Frete"/"Qtd. CTe"/"Orçado" (que
  // vêm 0 por padrão pras UFs sem CT-e) pintavam essas UFs de verde claro,
  // diferente do cinza usado por Frete/NF e Frete/Kg pra ausência de dado.
  // QA (PO1-2635) — "Orçado" vem de fonte separada (GoBID Savings/Mongo) e
  // pode ter valor > 0 mesmo com Qtd. CTe = 0 (orçamento sem execução no
  // período), o que pintava a UF na aba Orçado apesar de "sem operação".
  // Checar qtdCte também garante cinza uniforme nas 5 abas.
  const valueByUF = data.reduce((acc, item) => {
    if (!item.sigla) return acc;
    if (!item.qtdCte) return acc;
    const val = metric.getValue(item);
    if (val != null && val !== 0) acc[item.sigla.toLowerCase()] = val;
    return acc;
  }, {});

  const allValues = data
    .map((d) => metric.getValue(d))
    .filter((v) => v != null && v !== 0);
  const maxVal = allValues.length ? Math.max(...allValues) : 1;
  const minVal = allValues.length ? Math.min(...allValues) : 0;

  const handleDateInicial = (e) => setDataInicial(e);
  const handleDateFinal = (e) => setDataFinal(e);
  const toggleModalFilters = () => setModalFilters(!modalFilters);

  const onSubmit = useCallback(() => {
    setAuxDtInicial(dataInicial);
    setAuxDtFinal(dataFinal);
    setModalFilters(false);
  }, [dataFinal, dataInicial]);

  const emptyFill  = darkMode ? "#3d3d5c" : "#e4e4e4";
  const strokeColor = darkMode ? "#555" : "#ccc";
  const textColor  = darkMode ? "#e9ecef" : "#333";

  // PO1-2635 — linhas da tabela: filtro pela UF selecionada no mapa +
  // ordenação pelo estado `sort`. A tabela em si (`data`) não muda com a aba.
  const filteredRows = selected
    ? data.filter((d) => d.sigla && d.sigla.toLowerCase() === selected)
    : data;
  const sortedRows = sortRows(filteredRows, sort);

  return (
    <Card
      style={{ boxShadow: "0px 1px 20px 0px rgb(0, 0, 0)" }}
      className="card-chart"
    >
      <ModalFilters
        open={modalFilters}
        toggle={toggleModalFilters}
        handleDateInicial={handleDateInicial}
        dataInicial={dataInicial}
        handleDateFinal={handleDateFinal}
        dataFinal={dataFinal}
        onSubmit={() => onSubmit()}
      />
      <CardHeader>
        <Row style={{ alignItems: "center" }}>
          <Col>
            <h4>Frete por Estado — Mapa</h4>
          </Col>
          <Col style={{ textAlign: "right" }}>
            {selected && (
              <Button
                className="btn-sm mr-2"
                color="secondary"
                onClick={() => setSelected(null)}
              >
                Limpar seleção
              </Button>
            )}
            <Button
              className="btn-sm mr-2"
              color="info"
              outline
              onClick={handleExport}
              disabled={exporting || loading || data.length === 0}
            >
              <i className="tim-icons icon-cloud-download-93" />{" "}
              {exporting ? "Gerando..." : "Exportar"}
            </Button>
            <Button
              className="btn-sm dashboard-period-button"
              color="info"
              onClick={toggleModalFilters}
            >
              Período
            </Button>
          </Col>
        </Row>
        <Row style={{ marginTop: 10, alignItems: "center" }}>
          <Col>
            <ButtonGroup size="sm">
              {METRICS.map((m, idx) => (
                <Button
                  key={m.key}
                  className={activeMetric === idx ? "theme-accent-button" : ""}
                  color={activeMetric === idx ? "info" : "secondary"}
                  outline={activeMetric !== idx}
                  onClick={() => handleMetricChange(idx)}
                >
                  {m.label}
                </Button>
              ))}
            </ButtonGroup>
          </Col>
        </Row>
        <CardTitle style={{ marginTop: 6 }}>
          Distribuição geográfica de <strong>{metric.label}</strong> por UF
        </CardTitle>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div style={{ textAlign: "center" }}>
            <ClipLoader sizeUnit="px" size={90} color="#1d8cf8" loading={loading} />
            <br /><br />
            <h1 className="text-info" style={{ textAlign: "center" }}>Carregando ...</h1>
          </div>
        ) : data.length === 0 ? (
          <div style={{ textAlign: "center" }}>
            <h5>Nenhum dado para apresentar!</h5>
          </div>
        ) : (
          <Row className="justify-content-center">
            {/* Mapa */}
            <Col md={7} style={{ position: "relative" }}>
              <svg
                ref={svgRef}
                viewBox={`0 0 ${MAP_W} ${MAP_H}`}
                style={{ width: "100%", height: "420px" }}
                onMouseMove={(e) => {
                  const code = e.target.dataset.code;
                  if (code && paths[code]) {
                    const val = valueByUF[code];
                    const { clientX, clientY } = e.nativeEvent;
                    setTooltip({ x: clientX, y: clientY, name: paths[code].name, val });
                  } else {
                    setTooltip(null);
                  }
                }}
                onMouseLeave={() => setTooltip(null)}
                onClick={(e) => {
                  const code = e.target.dataset.code;
                  if (code && valueByUF[code] != null) {
                    setSelected((prev) => (prev === code ? null : code));
                  }
                }}
              >
                {Object.entries(paths).map(([code, { path }]) => {
                  const val = valueByUF[code];
                  const isSelected = selected === code;
                  const fill = isSelected
                    ? COLOR_SEL
                    : val != null
                    ? interpolateColor(val / maxVal)
                    : emptyFill;
                  return (
                    <path
                      key={code}
                      data-code={code}
                      d={path}
                      fill={fill}
                      stroke={isSelected ? "#fff" : strokeColor}
                      strokeWidth={isSelected ? 600 : 200}
                      style={{
                        cursor: val != null ? "pointer" : "default",
                        transition: "fill 0.2s",
                      }}
                    />
                  );
                })}
              </svg>

              {/* Tooltip */}
              {tooltip && (
                <div
                  style={{
                    position: "fixed",
                    left: tooltip.x + 14,
                    top: tooltip.y - 36,
                    background: darkMode ? "#e9ecef" : "#1e1e2f",
                    color: darkMode ? "#000" : "#fff",
                    padding: "6px 10px",
                    borderRadius: 4,
                    fontSize: "0.8rem",
                    pointerEvents: "none",
                    whiteSpace: "nowrap",
                    zIndex: 9999,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                  }}
                >
                  <strong>{tooltip.name}</strong>
                  <br />
                  {tooltip.val != null
                    ? metric.format(tooltip.val)
                    : "Sem dados"}
                </div>
              )}

              {/* Legenda de cores */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                <span style={{ fontSize: "0.72rem", color: textColor, whiteSpace: "nowrap" }}>
                  {allValues.length ? metric.format(minVal) : metric.emptyLabel}
                </span>
                <div
                  style={{
                    flex: 1,
                    height: 12,
                    borderRadius: 6,
                    background: `linear-gradient(to right, rgb(${COLOR_START.r},${COLOR_START.g},${COLOR_START.b}), rgb(${COLOR_END.r},${COLOR_END.g},${COLOR_END.b}))`,
                  }}
                />
                <span style={{ fontSize: "0.72rem", color: textColor, whiteSpace: "nowrap" }}>
                  {allValues.length ? metric.format(maxVal) : metric.emptyLabel}
                </span>
              </div>
              <div style={{ textAlign: "center", fontSize: "0.68rem", color: textColor, marginTop: 2, opacity: 0.7 }}>
                Clique em um estado para destacar
              </div>
            </Col>

            {/* Tabela fixa multi-métrica (PO1-2635) */}
            <Col md={5}>
              {selected && (
                <div style={{ marginBottom: 8, fontSize: "0.8rem", color: textColor }}>
                  Exibindo:{" "}
                  <strong style={{ color: COLOR_SEL }}>
                    {paths[selected]?.name || selected.toUpperCase()}
                  </strong>
                </div>
              )}
              <div style={{ maxHeight: "420px", overflow: "auto" }}>
                <table className="table table-sm" style={{ fontSize: "0.8rem" }}>
                  <thead>
                    <tr>
                      <th>
                        <SortHeader
                          label="UF"
                          field="sigla"
                          sort={sort}
                          onSort={handleSort}
                          align="left"
                        />
                      </th>
                      {activeColumns.map((col) => (
                        <th
                          key={col.field}
                          style={
                            col.field === metric.key
                              ? { background: "var(--everlog-card-surface-pill)" }
                              : undefined
                          }
                        >
                          <SortHeader
                            label={col.label}
                            field={col.field}
                            sort={sort}
                            onSort={handleSort}
                            align="right"
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((item) => (
                      <tr
                        key={item.sigla}
                        title={`Ver o detalhe de ${item.sigla}`}
                        onClick={() => {
                          setSelected(item.sigla.toLowerCase());
                          setDetalheUf(item);
                        }}
                        style={{
                          cursor: "pointer",
                          ...(selected && item.sigla.toLowerCase() === selected
                            ? {
                                background: darkMode ? "#4a1a24" : "#ffe0e6",
                                fontWeight: "bold",
                              }
                            : {}),
                        }}
                      >
                        <td>{item.sigla}</td>
                        {activeColumns.map((col) => (
                          <td
                            key={col.field}
                            style={{
                              textAlign: "right",
                              whiteSpace: "nowrap",
                              ...(col.field === metric.key
                                ? { fontWeight: 700 }
                                : {}),
                            }}
                          >
                            {item[col.field] != null ? col.format(item[col.field]) : "—"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>
        )}
      </CardBody>

      <Modal isOpen={!!detalheUf} toggle={() => setDetalheUf(null)} modalClassName="modal-align-top" fade={false}>
        {detalheUf ? (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "var(--everlog-card-hero-bg)",
                borderBottom: "1px solid var(--everlog-card-border)",
                padding: "16px 20px",
              }}
            >
              <div>
                <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, fontSize: 15 }}>
                  Frete com destino {detalheUf.sigla}
                </div>
                <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: 12 }}>
                  {dataInicial.format("DD/MM/YYYY")} a {dataFinal.format("DD/MM/YYYY")}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDetalheUf(null)}
                style={{ background: "none", border: "none", color: AUTOPILOT_COLORS.title, fontSize: 22, lineHeight: 1, cursor: "pointer", padding: "0 4px" }}
                aria-label="Fechar"
              >
                ×
              </button>
            </div>
            <ModalBody>
              <table className="table table-sm" style={{ marginBottom: "1rem" }} data-testid="detalhe-uf">
                <tbody>
                  {Object.keys(COLUMN_DEFS).map((field) => (
                    <tr key={field}>
                      <td style={{ color: AUTOPILOT_COLORS.muted }}>{COLUMN_DEFS[field].label}</td>
                      <td style={{ color: AUTOPILOT_COLORS.title, textAlign: "right", fontWeight: field === metric.key ? 700 : 500 }}>
                        {detalheUf[field] != null ? COLUMN_DEFS[field].format(detalheUf[field]) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", flexWrap: "wrap" }}>
                <Button
                  type="button"
                  size="sm"
                  color="info"
                  onClick={() => history.push(`/app/gobid/operacao/listagem-cotacoes?uf=${detalheUf.sigla}`)}
                >
                  Ver cotações com destino {detalheUf.sigla}
                </Button>
              </div>
            </ModalBody>
          </>
        ) : null}
      </Modal>
    </Card>
  );
}
